<template>
    <section dir="rtl" id="tools" class="relative py-20 md:py-28 overflow-hidden" :style="{ backgroundColor: '#F6F9FC' }">
        <div class="pointer-events-none absolute inset-0">
            <div class="absolute top-16 right-8 font-mono text-7xl md:text-8xl font-extrabold text-[rgba(230,57,70,0.04)] select-none">&lt;/&gt;</div>
            <div class="absolute bottom-20 left-10 font-mono text-6xl md:text-7xl font-extrabold text-[rgba(242,107,90,0.05)] select-none">{ }</div>
            <div class="absolute top-1/2 left-1/4 font-mono text-xs text-[rgba(230,57,70,0.07)] select-none tracking-widest">01001010 11010101 01110100</div>
            <div class="absolute -top-20 -left-20 h-80 w-80 rounded-full bg-[rgba(230,57,70,0.08)] blur-3xl" />
            <div class="absolute -bottom-20 -right-20 h-80 w-80 rounded-full bg-[rgba(242,107,90,0.08)] blur-3xl" />
        </div>

        <div class="relative mx-auto max-w-7xl px-5 sm:px-8">
            <div class="text-center max-w-3xl mx-auto">
                <span class="inline-flex items-center px-4 py-1.5 rounded-full bg-[rgba(230,57,70,0.1)] text-[#E63946] text-sm font-bold">
                    منظومة الأدوات
                </span>
                <h2 class="mt-5 text-[2rem] md:text-[3.15rem] font-extrabold leading-[1.45] md:leading-[1.5]" :style="{ color: '#1A0E10' }">
                    كل اللي محتاجه عشان تفهم وتطبّق
                    <span class="block mt-2 md:mt-2.5 bg-gradient-to-l from-[#E63946] to-[#F26B5A] bg-clip-text text-transparent leading-[1.5] md:leading-[1.55] pb-[0.12em]">
                        في مكان واحد
                    </span>
                </h2>
                <p class="mt-5 text-base md:text-lg leading-relaxed" :style="{ color: 'rgba(11,18,32,0.65)' }">
                    من الشرح للتدريب العملي… البرمجة مع غنيم بتديك أدوات تساعدك تذاكر، تكتب كود، تحل، وتعرف مستواك خطوة بخطوة.
                </p>
            </div>

            <div class="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 relative">
                <div class="hidden lg:block pointer-events-none absolute top-[88px] right-[12%] left-[12%] h-px bg-gradient-to-l from-[rgba(230,57,70,0)] via-[rgba(230,57,70,0.2)] to-[rgba(242,107,90,0)]" />

                <div
                    v-for="(t, i) in toolsData"
                    :key="t.title"
                    class="group relative bg-white border border-[rgba(26,14,16,0.05)] shadow-[0_10px_40px_-15px_rgba(11,18,32,0.12)] hover:shadow-[0_20px_50px_-15px_rgba(37,99,235,0.22)] hover:-translate-y-1.5 transition-all duration-300 flex flex-col"
                    :style="{ borderRadius: '28px' }"
                >
                    <span class="absolute top-5 left-5 font-mono text-xs font-extrabold" :style="{ color: `${t.accent}99` }">
                        0{{ i + 1 }}
                    </span>

                    <div class="p-6 pb-5">
                        <div
                            class="h-14 w-14 rounded-2xl flex items-center justify-center text-white shadow-lg"
                            :style="{
                                background: `linear-gradient(135deg, ${t.accent}, ${t.accent}cc)`,
                                boxShadow: `0 12px 28px -10px ${t.accent}80`,
                            }"
                        >
                            <lucide-icon :name="t.icon" class="h-6 w-6" />
                        </div>

                        <h3 class="mt-5 text-lg font-extrabold" :style="{ color: '#1A0E10' }">
                            {{ t.title }}
                        </h3>
                        <p class="mt-2 text-sm leading-relaxed" :style="{ color: 'rgba(11,18,32,0.65)' }">
                            {{ t.text }}
                        </p>
                    </div>

                    <div :class="`mx-6 mb-5 p-4 rounded-2xl bg-gradient-to-br ${t.tint} border border-[rgba(26,14,16,0.05)]`">
                        <div v-if="t.mock === 'book'" class="space-y-2">
                            <div class="flex items-center gap-2">
                                <div class="h-6 w-6 rounded-md bg-[rgba(230,57,70,0.15)] flex items-center justify-center">
                                    <lucide-icon name="BookMarked" class="h-3.5 w-3.5 text-[#E63946]" />
                                </div>
                                <div class="h-2 flex-1 rounded-full bg-[rgba(26,14,16,0.1)]" />
                            </div>
                            <div class="h-2 w-5/6 rounded-full bg-[rgba(26,14,16,0.08)]" />
                            <div class="h-2 w-4/6 rounded-full bg-[rgba(26,14,16,0.08)]" />
                            <div class="mt-3 flex items-center gap-2">
                                <div class="h-1.5 flex-1 rounded-full bg-[rgba(230,57,70,0.2)] overflow-hidden">
                                    <div class="h-full w-2/3 bg-[#E63946] rounded-full" />
                                </div>
                                <span class="text-[10px] font-mono font-bold text-[#E63946]">66%</span>
                            </div>
                        </div>

                        <div v-else-if="t.mock === 'code'" dir="ltr" class="rounded-xl bg-[#1A0E10] p-3 font-mono text-[10px] leading-relaxed">
                            <div class="flex items-center gap-1.5 mb-2">
                                <span class="h-2 w-2 rounded-full bg-[rgba(248,113,113,0.8)]" />
                                <span class="h-2 w-2 rounded-full bg-[rgba(250,204,21,0.8)]" />
                                <span class="h-2 w-2 rounded-full bg-[rgba(74,222,128,0.8)]" />
                            </div>
                            <div class="text-[#F26B5A]">function <span class="text-white">solve</span>() {</div>
                            <div class="text-white-70 pl-3">return <span class="text-[#22C55E]">"correct"</span>;</div>
                            <div class="text-[#F26B5A]">}</div>
                        </div>

                        <div v-else-if="t.mock === 'quiz'" class="space-y-2">
                            <div v-for="(q, qi) in quizRows" :key="qi" class="flex items-center gap-2">
                                <span :class="`h-5 w-5 rounded-md flex items-center justify-center ${q.ok ? 'bg-[rgba(34,197,94,0.15)] text-[#22C55E]' : 'bg-[rgba(26,14,16,0.08)] text-[rgba(26,14,16,0.4)]'}`">
                                    <lucide-icon name="CheckCircle2" class="h-3 w-3" />
                                </span>
                                <div :class="`h-2 ${q.w} rounded-full bg-[rgba(26,14,16,0.1)]`" />
                            </div>
                            <div class="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[rgba(34,197,94,0.15)] text-[#22C55E] text-[10px] font-extrabold font-mono">
                                85/100
                            </div>
                        </div>

                        <div v-else-if="t.mock === 'calendar'" class="grid grid-cols-7 gap-1">
                            <div
                                v-for="(cell, ci) in calendarCells"
                                :key="ci"
                                :class="`h-5 rounded-md ${
                                    cell.today
                                        ? 'bg-[#E63946] ring-2 ring-[rgba(230,57,70,0.3)]'
                                        : cell.active
                                        ? 'bg-[rgba(242,107,90,0.4)]'
                                        : 'bg-[rgba(26,14,16,0.06)]'
                                }`"
                            />
                        </div>
                    </div>

                    <a
                        :href="LINKTREE"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="mt-auto mx-6 mb-6 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm text-white transition-all hover:gap-3"
                        :style="{ background: `linear-gradient(135deg, ${t.accent}, ${t.accent}d9)` }"
                    >
                        {{ t.cta }}
                        <lucide-icon name="ArrowLeft" class="h-4 w-4" />
                    </a>
                </div>
            </div>

            <div class="mt-14 relative">
                <div class="relative mx-auto max-w-4xl rounded-3xl p-7 md:p-8 bg-white border border-[rgba(26,14,16,0.05)] shadow-[0_15px_40px_-15px_rgba(11,18,32,0.12)] text-center overflow-hidden">
                    <div class="pointer-events-none absolute inset-0 tools-closing-banner" />
                    <div class="relative tools-closing-icon-row">
                        <span
                            v-for="(icon, idx) in closingIcons"
                            :key="icon.name"
                            :class="['tools-closing-icon', `tools-closing-icon--${idx}`]"
                        >
                            <lucide-icon :name="icon.name" class="h-4 w-4 text-white" />
                        </span>
                    </div>
                    <p class="relative text-lg md:text-xl font-extrabold leading-relaxed" :style="{ color: '#1A0E10' }">
                        اتفرج، افهم، حل، اتدرّب…
                        <span class="tools-closing-text-gradient">
                            وشوف مستواك قبل الامتحان.
                        </span>
                    </p>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import LucideIcon from './LucideIcon.vue';
import { toolsData, LINKTREE } from './landing-data';

export default {
    name: 'WelcomeToolsEcosystem',
    components: { LucideIcon },
    data() {
        return {
            toolsData,
            LINKTREE,
            quizRows: [
                { ok: true, w: 'w-5/6' },
                { ok: true, w: 'w-4/6' },
                { ok: false, w: 'w-3/6' },
            ],
            closingIcons: [
                { name: 'BookOpen' },
                { name: 'Brain' },
                { name: 'ClipboardCheck' },
                { name: 'Code2' },
            ],
        };
    },
    computed: {
        calendarCells() {
            return Array.from({ length: 14 }, (_, i) => ({
                active: [2, 5, 8, 11].includes(i),
                today: i === 5,
            }));
        },
    },
};
</script>
